import React, { useState, useEffect } from 'react';
import { 
  Users, MessageSquare, Bot, Activity, TrendingUp, HelpCircle, 
  Download, RefreshCw, Layers, ArrowUpRight 
} from 'lucide-react';
import { getAnalyticsOverview, getAnalyticsFaqs, downloadCsvExport } from './adminApi';

export default function DashboardOverview({ onSelectFaq }) {
  const [data, setData] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [overview, faqsData] = await Promise.all([
        getAnalyticsOverview(),
        getAnalyticsFaqs(10),
      ]);
      setData(overview);
      setFaqs(faqsData.faqs || []);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadCsvExport();
    } catch (err) {
      alert('Failed to export conversations: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16 text-gray-500">
        <RefreshCw className="w-8 h-8 animate-spin text-[#668f45] mb-3" />
        <p className="font-medium text-sm">Calculating conversation analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center bg-red-50 rounded-2xl border border-red-200 text-red-700">
        <p className="font-semibold mb-2">Error loading analytics</p>
        <p className="text-sm mb-4">{error}</p>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const topicEntries = Object.entries(data?.topic_breakdown || {});
  const totalTopicHits = topicEntries.reduce((sum, [, count]) => sum + count, 0);

  return (
    <div className="space-y-8">
      {/* Header controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Analytics & Intent Intelligence</h2>
          <p className="text-sm text-gray-500">
            Insights gathered from anonymous visitor conversations and AI interactions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#668f45] text-white rounded-xl text-sm font-semibold hover:bg-[#5b803e] transition-all shadow-md shadow-[#668f45]/20 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export Conversations (CSV)'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Visitor Sessions</span>
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-gray-900">{data?.total_sessions || 0}</div>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <span className="text-emerald-600 font-semibold">{data?.active_last_24h || 0} active</span>
            <span>in last 24 hours</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Questions</span>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <MessageSquare className="w-5 h-5" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-gray-900">{data?.total_queries || 0}</div>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <span>Visitor prompts asked</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Bot Responses</span>
            <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-gray-900">{data?.total_responses || 0}</div>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <span>TEDRA AI answers given</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Engagement Depth</span>
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <Activity className="w-5 h-5" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-gray-900">{data?.avg_messages_per_session || 0}</div>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <span>Avg messages / conversation</span>
          </div>
        </div>
      </div>

      {/* 7-Day Trend Chart & Topic Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Sessions Trend */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#668f45]" />
                <h3 className="font-bold text-gray-900">7-Day Chat Volume Trend</h3>
              </div>
              <span className="text-xs text-gray-400 font-medium">Daily Visitor Sessions</span>
            </div>

            {/* Simple CSS Bar Chart */}
            <div className="h-48 flex items-end justify-between gap-3 pt-4 border-b border-gray-100">
              {(data?.trends || []).map((t, idx) => {
                const maxSessions = Math.max(...(data.trends.map(d => d.sessions)), 1);
                const heightPct = Math.max(12, Math.round((t.sessions / maxSessions) * 100));
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-2 group h-full justify-end">
                    <span className="text-xs font-semibold text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      {t.sessions}
                    </span>
                    <div
                      className="w-full max-w-[40px] bg-gradient-to-t from-[#668f45] to-[#8ac35d] rounded-t-lg group-hover:from-[#567a39] group-hover:to-[#78ab50] transition-all"
                      style={{ height: `${heightPct}%` }}
                    />
                    <span className="text-[11px] font-medium text-gray-400 group-hover:text-gray-700 transition-colors">
                      {t.date.split('-').slice(1).join('/')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 pt-3 flex items-center justify-between text-xs text-gray-500">
            <span>Weekly Active Sessions: <strong className="text-gray-800">{data?.active_last_7d || 0}</strong></span>
            <span>Refreshed live</span>
          </div>
        </div>

        {/* Topic Breakdown */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <Layers className="w-5 h-5 text-[#668f45]" />
            <h3 className="font-bold text-gray-900">User Inquiries by Topic</h3>
          </div>

          <div className="space-y-4">
            {topicEntries.map(([topic, count], idx) => {
              const pct = totalTopicHits > 0 ? Math.round((count / totalTopicHits) * 100) : 0;
              return (
                <div key={idx}>
                  <div className="flex justify-between text-xs font-semibold text-gray-700 mb-1">
                    <span className="truncate pr-2">{topic}</span>
                    <span className="text-gray-500">{count} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div
                      className="bg-[#668f45] h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Frequently Asked Questions */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#668f45]" />
            <h3 className="font-bold text-gray-900">Top Recurring Questions & Visitor Intent</h3>
          </div>
          <span className="text-xs text-gray-400">Aggregated from session logs</span>
        </div>

        {faqs.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-6">
            No queries logged yet. Start chatting with TEDRA to populate this list.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                onClick={() => onSelectFaq && onSelectFaq(faq.original)}
                className="flex items-center justify-between p-3.5 bg-gray-50 hover:bg-[#668f45]/5 hover:border-[#668f45]/30 border border-gray-200/80 rounded-xl transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-3 min-w-0 pr-3">
                  <span className="w-6 h-6 rounded-lg bg-white text-gray-600 font-bold text-xs flex items-center justify-center border border-gray-200 flex-shrink-0 group-hover:border-[#668f45]/30 group-hover:text-[#668f45]">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-800 truncate group-hover:text-gray-900">
                    "{faq.original}"
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs px-2.5 py-1 bg-white font-bold rounded-lg text-[#668f45] border border-gray-200">
                    {faq.count} {faq.count === 1 ? 'time' : 'times'}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-[#668f45] transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
